import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { ORDER_TYPES, PTR_STATUS_VALUES, TERM_CHANGE_VALUES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { getUtcRangeForDate } from '@repo/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

/** The status orders sit at while they await O2C review. Carries a space; never derive a key from it. */
export const PAYMENT_REVIEW_STATUS = 'UNDER REVIEW'

/**
 * Payment terms are whole days. Fuze Core applies the SAP prefix on their side, so the control
 * submits a plain number as a string.
 */
export const PAYMENT_TERM_MIN_DAYS = 0
export const PAYMENT_TERM_MAX_DAYS = 30

export const PAYMENT_REVIEW_OPERATIONS = ['RELEASE', 'CANCEL', 'CHANGE'] as const
export type PaymentReviewOperation = (typeof PAYMENT_REVIEW_OPERATIONS)[number]

/**
 * Fuze Core takes at most this many orders in one action call, so "select all results" cannot
 * select beyond it. Mirrors the `.max(500)` on the backend's own body schema; changing one without
 * the other turns a selection the UI offered into a 400.
 */
export const PAYMENT_REVIEW_ACTION_MAX_ORDERS = 500

/**
 * Row selection is keyed by this instead of the row index, so a selection survives paging and can
 * hold orders that are not on screen. The two fields are exactly what the action endpoint needs, so
 * a selected key is a submittable target on its own and no row object has to be kept alongside it.
 */
export const paymentReviewRowId = (order: { poNumber?: string | null; sapErp?: string | null }) =>
  `${order.sapErp ?? ''}|${order.poNumber ?? ''}`

/** Splits on the first separator only, so a PO number containing one is not truncated. */
export function paymentReviewTargetFromRowId(id: string) {
  const at = id.indexOf('|')
  return at === -1
    ? { sapErp: '', poNumber: '' }
    : { sapErp: id.slice(0, at), poNumber: id.slice(at + 1) }
}

export const paymentReviewSearchSchema = z.object({
  keyword: z.string().optional(),
  date: z.string().optional(),
  // No `status`. The screen filters on the review decision alone, and a stale `?status=` in a
  // bookmarked URL would otherwise keep filtering server-side with no control showing it.
  ptrStatus: z.array(z.enum(PTR_STATUS_VALUES)).optional(),
  termChange: z.array(z.enum(TERM_CHANGE_VALUES)).optional(),
  injInitiative: z.array(z.string()).optional(),
  // No `source`. The screen offers no source filter, and leaving the field parseable would let a
  // stale `?source=` in a bookmarked URL keep filtering server-side with no control showing it.
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  territory: z.array(z.string()).optional(),
  customer: z.array(z.string()).optional(),
  banner: z.array(z.string()).optional(),
  outletSubtype: z.array(z.string()).optional(),
  type: z.enum(ORDER_TYPES).optional(),
  ...datatableSearchSchema.shape,
})

export type PaymentReviewSearch = z.infer<typeof paymentReviewSearchSchema>

async function getPaymentReviewOrders(search: PaymentReviewSearch, signal?: AbortSignal) {
  const api = getApi()

  const query = { ...search } as PaymentReviewSearch & {
    dateStartUtc: string | undefined
    dateEndUtc: string | undefined
  }

  if (search.date) {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const utcRangeForDate = getUtcRangeForDate(search.date, userTimeZone)

    if (utcRangeForDate.start && utcRangeForDate.end) {
      query.dateStartUtc = utcRangeForDate.start
      query.dateEndUtc = utcRangeForDate.end
    }
  }

  const res = await api.orders['payment-review'].$get(
    { query: stringifyValues(query) },
    { init: { signal } },
  )

  return await res.json()
}

export function usePaymentReviewOrders(queries: PaymentReviewSearch) {
  return useQuery({
    queryKey: ['paymentReviewOrders', queries],
    queryFn: ({ signal }) => getPaymentReviewOrders(queries, signal),
  })
}

async function getPaymentReviewInitiatives(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.orders['payment-review'].initiatives.$get({}, { init: { signal } })

  return await res.json()
}

/** The injection files behind the orders under review, which the initiative filter offers. */
export function usePaymentReviewInitiatives() {
  return useQuery({
    queryKey: ['paymentReviewInitiatives'],
    queryFn: ({ signal }) => getPaymentReviewInitiatives(signal),
  })
}

async function getPaymentReviewCsv(search: PaymentReviewSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  return await fetchCsvExport(`/orders/payment-review/export?${queryParams}`, signal)
}

export const paymentReviewExportQueryOptions = (queries: PaymentReviewSearch) => ({
  queryKey: ['paymentReviewExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getPaymentReviewCsv(queries, signal),
})

async function getPaymentReviewReportCsv(search: PaymentReviewSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  return await fetchCsvExport(`/orders/payment-review/report?${queryParams}`, signal)
}

/** The report: every status and every table column, for what the filters match. */
export const paymentReviewReportQueryOptions = (queries: PaymentReviewSearch) => ({
  queryKey: ['paymentReviewReport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getPaymentReviewReportCsv(queries, signal),
})

/**
 * Every reviewable order matching the current filters, for "select all results". The list endpoint
 * pages, so this asks for a single page the size of the action limit rather than walking pages:
 * anything past that limit could not be submitted in one call anyway. Orders in another status are
 * dropped here for the same reason their row checkbox is disabled, since releasing an
 * already-released order is meaningless and Fuze Core would skip it.
 */
export const paymentReviewTargetsQueryOptions = (search: PaymentReviewSearch) => ({
  queryKey: ['paymentReviewTargets', search],
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const data = await getPaymentReviewOrders(
      { ...search, page: 1, pageSize: PAYMENT_REVIEW_ACTION_MAX_ORDERS },
      signal,
    )

    return data.results
      .filter((order) => order.status === PAYMENT_REVIEW_STATUS)
      .map((order) => ({ poNumber: order.poNumber ?? '', sapErp: order.sapErp ?? '' }))
      .filter((target) => target.poNumber !== '' && target.sapErp !== '')
  },
})

export type PaymentReviewActionBody = {
  operation: PaymentReviewOperation
  paymentTerms: string
  orders: { poNumber: string; sapErp: string }[]
}

async function submitPaymentReviewAction(body: PaymentReviewActionBody) {
  const api = getApi()
  const res = await api.orders['payment-review'].action.$post({ json: body })

  return await res.json()
}

/**
 * Fuze Core answers 200 even when it acted on nothing, reporting per-order outcomes instead. The
 * toast reflects what actually happened rather than the HTTP status, so a run where every order was
 * skipped reads as a failure to the user.
 */
export function usePaymentReviewAction({ handleOnSuccess, handleOnError }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['orders', 'common'])

  return useMutation({
    mutationKey: ['orders-payment-review-action'],
    mutationFn: submitPaymentReviewAction,
    onSuccess: async (result) => {
      /**
       * `orderDetails` is keyed by dynamo id alone (`orderDetailsQueryOptions`), and an action
       * carries only PO number and ERP, so there is nothing to build a precise key from. This used
       * to invalidate `['orderDetails', sapErp, poNumber]`, which matched no query at all: a filter
       * matches by key prefix, and no order-details key has a second or third element to compare.
       * The change landed in the database and the open page kept showing the old term until it was
       * reloaded by hand. Invalidating the whole collection is what the uncouple and delivery-date
       * mutations already do, and an order-details query is small.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['paymentReviewOrders'] }),
        queryClient.invalidateQueries({ queryKey: ['orderDetails'] }),
      ])

      const acted = result.released + result.cancelled + result.changed
      const skipped = result.skipped.length

      if (acted === 0 && skipped > 0) {
        toast.error(t('toast.error', { ns: 'common' }), {
          description: t('paymentReview.toast.allSkipped', {
            count: skipped,
            reason: result.skipped[0]?.reason ?? '',
          }),
          className: toastClassName,
        })
        handleOnError?.()
        return
      }

      handleOnSuccess?.()

      toast.success(t('toast.success', { ns: 'common' }), {
        description:
          skipped > 0
            ? t('paymentReview.toast.partial', { acted, skipped })
            : t('paymentReview.toast.success', { count: acted }),
        className: toastClassName,
      })
    },
    onError: (error) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: error.message,
        className: toastClassName,
      })

      handleOnError?.()
    },
  })
}
